"use client";

import { errorMessage } from "@/lib/error-message";

import type { OAuthProvider } from "./oauth-providers";
import {
  completeNativeSignIn,
  reportNativeSignInFailure,
} from "./native-sign-in-actions";
import { POST_AUTH_DEFAULT, safeNext } from "./safe-next";

/** Which providers can sign in through a NATIVE system sheet in the shell,
 *  and how to run each one.
 *
 *  This is the client-side half of the OAUTH_PROVIDERS registry, kept
 *  separate because that module is imported by Server Actions and must stay
 *  plain data — the runners here reach for `window.Capacitor` and pull in the
 *  Supabase browser client, neither of which belongs on the server. Same
 *  split, and same reason, as the icon map in components/forms/oauth-button. */

/** Shared by every runner so the form can handle them uniformly.
 *  `unavailable` means "this shell can't do it" (a build predating the
 *  plugin, or a non-secure origin) and is a routine fall-through to the
 *  redirect, not an error worth showing anyone. */
export type NativeSignInResult =
  /** `accountCreating` is the runner's answer to "did this sign-in create
   *  the account" — it already has the user, so asking here saves the
   *  common case (a returning user) a server round trip it never needed. */
  | { status: "signed_in"; accountCreating: boolean }
  | { status: "cancelled" }
  | { status: "unavailable" }
  | { status: "failed"; message: string; code?: string };

/** DYNAMIC import, and that is load-bearing rather than stylistic: the
 *  shared runner behind both loaders pulls the Supabase browser client
 *  (~59 KB gzip, measured), and it is the only thing in apps/web that puts
 *  Supabase in a browser bundle at all. A static import would put that on
 *  /login and /signup for every visitor, to serve a path that only runs
 *  when someone taps a provider button inside the iOS shell.
 *
 *  Every loader imports the SAME module, native-runners, so there is one
 *  provider chunk — two entries meant two chunks each carrying its own copy
 *  of the `buffer` polyfill. Add the next provider the SAME way — a loader
 *  whose body does the `import()`, never a top-level import of a runner.
 *  One static edge from this module re-merges the chunk and silently undoes
 *  the split for every visitor. Keep the specifier a string literal too:
 *  `import(someVar)` bundles every candidate instead of splitting.
 *
 *  Exhaustive Record, not Partial: a new provider then fails to compile
 *  until someone decides whether it has a native sheet, instead of silently
 *  defaulting to the redirect. A runner that cannot run reports
 *  `unavailable`, which IS the redirect. */
const NATIVE_SIGN_IN_LOADERS: Record<
  OAuthProvider,
  () => Promise<() => Promise<NativeSignInResult>>
> = {
  apple: async () => {
    const { runNativeAppleSignIn } = await import("./native-runners");
    return () =>
      runNativeAppleSignIn({ plugin: window.Capacitor?.Plugins?.SocialLogin });
  },
  google: async () => {
    const { runNativeGoogleSignIn } = await import("./native-runners");
    return () =>
      runNativeGoogleSignIn({ plugin: window.Capacitor?.Plugins?.SocialLogin });
  },
};

/** Runs a provider's native sheet AND the post-auth work that goes with it.
 *
 *  Both live here rather than inside a runner because this is the one place
 *  every provider passes through: a second native provider inherits the
 *  welcome email and the failure telemetry by existing, instead of by
 *  remembering to call them. It is also why nothing below hardcodes a
 *  provider name — the dispatcher already knows which one it ran.
 *
 *  Both actions are AWAITED: the caller navigates via `location.assign` the
 *  moment this resolves, and that aborts in-flight fetches. */
export async function runNativeSignIn(
  provider: OAuthProvider,
): Promise<NativeSignInResult> {
  // NEVER rejects. The form's submit handler has no catch, so a rejection
  // here would leave the button pending forever with no redirect fallback
  // and no telemetry — and there are real ways to get one: the chunk fetch
  // (Turbopack caches a failed load as permanently rejected, so one blip
  // would stick until reload), or a runner throwing past its own catch.
  // Anything the runner did not turn into a result means the redirect flow
  // is the answer, exactly as for a shell that cannot run native at all.
  let result: NativeSignInResult;
  try {
    result = await (await NATIVE_SIGN_IN_LOADERS[provider]())();
  } catch (error) {
    console.warn(
      `[native-${provider}] attempt threw — using the redirect flow`,
      errorMessage(error),
    );
    return { status: "unavailable" };
  }

  // The post-auth work is best effort: the sign-in result stands whether or
  // not the welcome or the telemetry POST got through (both are fetches that
  // reject offline or on a stale action id after a deploy).
  try {
    if (result.status === "signed_in" && result.accountCreating) {
      await completeNativeSignIn();
    } else if (result.status === "failed") {
      await reportNativeSignInFailure(provider, result.code, result.message);
    }
  } catch (error) {
    console.warn(`[native-${provider}] post-auth step failed`, errorMessage(error));
  }
  return result;
}

/** What the form should DO about a native attempt — pure, so the branching
 *  is testable without a DOM. The component keeps only the navigation
 *  itself, which genuinely belongs to it. */
export type ShellSignInOutcome =
  | { kind: "navigate"; to: string }
  | { kind: "redirect_flow" }
  | { kind: "error"; message: string; code?: string }
  | { kind: "idle" };

export function resolveShellSignIn(
  result: NativeSignInResult,
  next: string | undefined,
): ShellSignInOutcome {
  switch (result.status) {
    case "signed_in":
      // Re-validated even though every caller already does: safe-next's own
      // doc calls that the convention, because this value reaches
      // location.assign and a future caller forgetting to sanitize would
      // otherwise have an open redirect.
      return { kind: "navigate", to: safeNext(next, POST_AUTH_DEFAULT) };
    case "cancelled":
      return { kind: "idle" };
    case "failed":
      return { kind: "error", message: result.message, code: result.code };
    case "unavailable":
      return { kind: "redirect_flow" };
  }
}

/** Fetch the chunk before the tap. In the shell it would otherwise be
 *  fetched from app.medceptor.com at tap time, delaying the system sheet on
 *  a slow connection; callers gate this on being in the shell, so web
 *  visitors never request it. */
export function preloadNativeSignIn(provider: OAuthProvider): void {
  void NATIVE_SIGN_IN_LOADERS[provider]();
}
